import { getPosition } from './base.utils.js';

export const followingSidebar = ({
  sidebar,
  contentSticky,
  sidebarInnerSelector = '.following-sidebar-inner',
  isHeaderCalc = false,
  gap = 130,
  bottomGap = gap,
  directionThreshold = 4,
  directionTransition = 260,
}) => {
  const sidebarInner = sidebar.querySelector(sidebarInnerSelector);

  if (!sidebarInner || !contentSticky) {
    return;
  }

  const sidebarBuffer = document.createElement('DIV');
  sidebarBuffer.classList.add('following-sidebar-buffer');
  sidebarInner.style.position = 'sticky';

  const header = document.querySelector('header');

  sidebar.insertAdjacentElement('afterbegin', sidebarBuffer);

  let lastScrollTop = 0;
  let isScrollingDown = null;
  let scrollFrame = null;
  let directionTimer = null;

  let headerHeight = 0;
  let sidebarHeight = 0;
  let availableHeight = 0;
  let topGap = 0;
  let contentStickyTop = getPosition(contentSticky).y;
  let isSidebarHigherViewport = false;

  const getCurrentScrollTop = () => window.scrollY || document.documentElement.scrollTop;

  function setStyleValue(element, property, value) {
    if (element.style[property] !== value) {
      element.style[property] = value;
    }
  }

  function updateMetrics(updateContentTop = false) {
    headerHeight = isHeaderCalc && header ? header.getBoundingClientRect().height : 0;
    sidebarHeight = sidebarInner.getBoundingClientRect().height;
    availableHeight = window.innerHeight - headerHeight;
    topGap = headerHeight + gap;

    if (updateContentTop) {
      contentStickyTop = getPosition(contentSticky).y;
    }

    isSidebarHigherViewport = sidebarHeight > availableHeight;
  }

  function isContentActive(scrollTop = getCurrentScrollTop()) {
    return scrollTop >= contentStickyTop - topGap;
  }

  function calcHeightOfSidebar(isScrollingDown) {
    updateMetrics(true);

    const sidebarInnerTop = getPosition(sidebarInner).y;

    setStyleValue(sidebarBuffer, 'height', `${Math.max(0, sidebarInnerTop - contentStickyTop)}px`);

    if (isScrollingDown) {
      setStyleValue(sidebarInner, 'top', '');
      setStyleValue(sidebarInner, 'bottom', `${
        -headerHeight -
        gap +
        window.innerHeight -
        sidebarHeight
      }px`);
    } else {
      setStyleValue(sidebarInner, 'bottom', '');
      setStyleValue(sidebarInner, 'top', `${
        Math.min(
          window.innerHeight - sidebarHeight,
          headerHeight
        ) - bottomGap
      }px`);
    }
  }

  function animateDirectionChange(isScrollingDown) {
    const prevTop = sidebarInner.getBoundingClientRect().top;

    calcHeightOfSidebar(isScrollingDown);

    const nextTop = sidebarInner.getBoundingClientRect().top;
    const offset = prevTop - nextTop;

    if (!offset) {
      return;
    }

    if (directionTimer) {
      window.clearTimeout(directionTimer);
    }

    setStyleValue(sidebarInner, 'willChange', 'transform');
    setStyleValue(sidebarInner, 'transition', 'none');
    setStyleValue(sidebarInner, 'transform', `translateY(${offset}px)`);
    void sidebarInner.offsetHeight;
    setStyleValue(sidebarInner, 'transition', `transform ${directionTransition}ms ease`);
    setStyleValue(sidebarInner, 'transform', 'translateY(0)');

    directionTimer = window.setTimeout(() => {
      setStyleValue(sidebarInner, 'transition', '');
      setStyleValue(sidebarInner, 'transform', '');
      setStyleValue(sidebarInner, 'willChange', '');
      directionTimer = null;
    }, directionTransition);
  }

  function resetSticky() {
    setStyleValue(sidebarInner, 'bottom', '');
    setStyleValue(sidebarInner, 'top', `${topGap}px`);
    setStyleValue(sidebarBuffer, 'height', '0');
  }

  updateMetrics(true);

  if (!isSidebarHigherViewport) {
    resetSticky();
  } else if (isContentActive()) {
    calcHeightOfSidebar(false);
  } else {
    resetSticky();
  }

  function onScroll() {
    scrollFrame = null;
    let st = getCurrentScrollTop();
    let scrollDelta = st - lastScrollTop;

    if (Math.abs(scrollDelta) < directionThreshold) {
      return;
    }

    if (!isContentActive(st)) {
      updateMetrics();

      resetSticky();
      isScrollingDown = null;
      lastScrollTop = st <= 0 ? 0 : st;
      return;
    }

    if (!isSidebarHigherViewport) {
      resetSticky();
      lastScrollTop = st <= 0 ? 0 : st;
      return;
    }

    if (scrollDelta > 0 && !isScrollingDown) {
      animateDirectionChange(isScrollingDown);
      isScrollingDown = true;
    } else if (scrollDelta < 0 && isScrollingDown) {
      animateDirectionChange(isScrollingDown);
      isScrollingDown = false;
    }

    lastScrollTop = st <= 0 ? 0 : st;
  }

  window.addEventListener('scroll', function () {
    if (scrollFrame) {
      return;
    }

    scrollFrame = requestAnimationFrame(onScroll);
  }, { passive: true });

  const updateSidebar = () => {
    updateMetrics(true);

    if (!isSidebarHigherViewport || !isContentActive()) {
      resetSticky();
      return;
    }

    calcHeightOfSidebar(true);
    calcHeightOfSidebar(false);
  };

  let windowWidth = window.innerWidth;

  window.addEventListener('resize', () => {
    if (windowWidth !== window.innerWidth) {
      windowWidth = window.innerWidth;
      updateSidebar();
    }
  });

  window.addEventListener('load', updateSidebar, { once: true });
};
